import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { BillingType, EntryType, useFetchCaseBillingLazyQuery } from '../../../generated'
import { AppContext } from '../../context'
import { ManualTimeEntryForm } from '../../interfaceTypes/forms'
import { capitalizeCase, removeUnderScore } from '../../utils'
import { getBillableStatus } from '../../utils/entry'

/**
 * Table shows entries type and their billable status for each case type:
 *
 * Case Type             | Time Entries                  | Legal Fees   | Expenses     | Costs (External
 * ----------------------|-------------------------------|--------------|--------------|----------------
 * Hourly	               | Billable                      | Billable     | Billable     | Billable
 * Flat Fee	             | Non-billable                  | Billable     | Billable     | Billable
 * Hybrid (Flat + Hourly)| Billable after flat-fee hours | Billable     | Billable     | Billable after flat-fee hours
 * Contingency	         | Non-billable                  | Non-billable | Billable     | Billable
 * Contingency Hybrid    | Non-billable                  | Non-billable | Billable     | Billable
 * Pro Bono	             | Non-billable                  | Non-billable | Non-billable | Billable
 * Consult	             | Non-billable                  | Non-billable | Non-billable | Non-billable
 */

interface BillableAndNoChargeCheckboxesProps {
  setValue: UseFormSetValue<ManualTimeEntryForm>
  control: Control<ManualTimeEntryForm>
  caseId?: string
}

const BillableAndNoChargeCheckboxes = ({ setValue, control, caseId }: BillableAndNoChargeCheckboxesProps) => {
  const { entryType } = useContext(AppContext)
  const [disabled, setDisabled] = useState(false)
  const [billingType, setBillingType] = useState<BillingType | undefined>()

  const getNote = () => {
    switch (entryType) {
      case EntryType.Time:
        return `Time entries are not billable in
    ${capitalizeCase(removeUnderScore(billingType?.toLowerCase() || ''))} matters`
      case EntryType.Legal:
        return `Legal fee is not billable in
    ${capitalizeCase(removeUnderScore(billingType?.toLowerCase() || ''))} matters`
      case EntryType.Expense:
        return `Expense entries are not billable in
    ${capitalizeCase(removeUnderScore(billingType?.toLowerCase() || ''))} matters`
      case EntryType.Cost:
        return `Investigator Time is not billable in
    ${capitalizeCase(removeUnderScore(billingType?.toLowerCase() || ''))} matters`
    }

    return `Entries are not billable in ${capitalizeCase(removeUnderScore(billingType?.toLowerCase() || ''))} matters`
  }

  const [fetchCase] = useFetchCaseBillingLazyQuery({
    onCompleted: ({ fetchCase: { case: caseData } }) => {
      const { billingType, isFlatFeeHoursExhausted } = caseData?.billing || {}
      const billable = getBillableStatus({ billingType, isFlatFeeHoursExhausted, entryType })

      setDisabled(!billable)
      setValue('billable', billingType === BillingType.ProBono ? false : billable)
      setBillingType(billingType)
    }
  })

  useEffect(() => {
    if (caseId) {
      fetchCase({ variables: { caseInput: { id: caseId } } })
      return
    }
    // No matter selected means no billing rule to apply. Without this the
    // greyed-out checkbox and its "not billable in … matters" note lingered
    // from the matter that was just cleared. The `billable` value itself is
    // left alone — the checkbox is editable again, so it simply reflects
    // whatever the form currently holds.
    setDisabled(false)
    setBillingType(undefined)
  }, [caseId, fetchCase])

  return (
    <>
      {disabled ? (
        <FormControlLabel control={<Checkbox disabled={disabled} checked={false} />} label="Billable" sx={{ mb: 2 }} />
      ) : (
        <Controller
          name="billable"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Billable" sx={{ mb: 2 }} />
          )}
        />
      )}

      <Controller
        name="showOnBill"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} />}
            label="Show on Bill"
            sx={{ mb: 2 }}
          />
        )}
      />

      {disabled && (
        <Typography variant="caption" component="p" mt={-2} mb={2}>
          Note: {getNote()}
        </Typography>
      )}
    </>
  )
}

export default BillableAndNoChargeCheckboxes
